import { useEffect, useState } from "react";
import { Bug, Check, Inbox, Lightbulb, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, Empty, btn } from "@/components/common";
import { cn } from "@/lib/utils";

const META = {
  bug: ["Bug", "bg-red-50 text-red-600", Bug],
  suggestion: ["Suggestion", "bg-amber-50 text-amber-700", Lightbulb],
  feedback: ["Feedback", "bg-sky-50 text-sky-600", MessageCircle],
};
const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso; } };

function Stat({ label, value, testid }) {
  return <Card className="p-5" data-testid={testid}><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</p><p className="mt-2 font-display text-3xl font-bold text-navy-900">{value}</p></Card>;
}

export default function FeedbackTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/feedback").then(({ data }) => setData(data)).catch(() => { toast.error("Couldn't load feedback — please refresh"); setData({ items: [], stats: { total: 0, bug: 0, suggestion: 0, feedback: 0 } }); });
  useEffect(() => { load(); }, []);
  const resolve = async (id) => { setBusy(id); try { await api.post(`/feedback/${id}/resolve`); await load(); } catch { toast.error("Couldn't resolve — please try again"); } setBusy(null); };

  if (!data) return <Card><div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-navy-700" /></div></Card>;
  const { items, stats } = data;

  return (
    <div className="space-y-4" data-testid="communication-feedback-page">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Total" value={stats.total} testid="feedback-stat-total" />
        <Stat label="Bugs" value={stats.bug} testid="feedback-stat-bug" />
        <Stat label="Suggestions" value={stats.suggestion} testid="feedback-stat-suggestion" />
        <Stat label="Feedback" value={stats.feedback} testid="feedback-stat-feedback" />
      </div>
      {items.length === 0 ? (
        <Card><Empty icon={Inbox} title="No feedback yet" text="Bug reports, suggestions and feedback submitted from either portal will show up here." /></Card>
      ) : (
        <div className="space-y-3">
          {items.map((f) => {
            const [label, cls, Icon] = META[f.type] || META.feedback;
            return (
              <Card key={f.id} className="p-4 sm:p-5" data-testid={`feedback-item-${f.id}`}>
                <div className="flex items-start gap-3">
                  <span className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-full", cls)}><Icon className="h-4 w-4" /></span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold", cls)}>{label}</span>
                      <span className="text-sm font-semibold text-navy-900">{f.submitterName}</span>
                      <span className="text-xs text-slate-400">{f.submitterKind === "client" ? "Client" : "Agent"} · {fmt(f.createdAt)}</span>
                      {f.status === "resolved" && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-600">Resolved</span>}
                    </div>
                    <p className="mt-1.5 whitespace-pre-line text-sm text-slate-700">{f.message}</p>
                    {f.page && <p className="mt-1 text-xs text-slate-400">Page: {f.page}</p>}
                  </div>
                  {f.status !== "resolved" && <button disabled={busy === f.id} onClick={() => resolve(f.id)} data-testid={`feedback-resolve-${f.id}`} className={`${btn.ghost} h-8`}><Check className="h-3.5 w-3.5" />Resolve</button>}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
